'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  deleteMyProfilePicture,
  fetchMyProfile,
  updateMyProfile,
  uploadMyProfilePicture,
  type MyProfileResponse,
  type UpdateMyProfileRequest,
} from '../api';

export const MY_PROFILE_QUERY_KEY = ['me', 'profile'] as const;

/**
 * Backend presigned URLs live for 5 minutes; refetch a bit before that
 * window expires so the avatar is always renderable from a fresh URL.
 */
const PROFILE_STALE_TIME_MS = 4 * 60 * 1000;

export function useMyProfile() {
  return useQuery<MyProfileResponse>({
    queryKey: MY_PROFILE_QUERY_KEY,
    queryFn: fetchMyProfile,
    staleTime: PROFILE_STALE_TIME_MS,
    refetchOnWindowFocus: true,
  });
}

export function useUploadMyProfilePicture() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadMyProfilePicture(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MY_PROFILE_QUERY_KEY });
    },
  });
}

/**
 * `PATCH /api/me/profile` self-service profile update (Epic 04 F4.1).
 *
 * Invalidates the profile query so the UI re-renders against the freshly
 * persisted server state. Callers that also display values cached in the
 * auth session (e.g. `useAuth().user.firstName`) should additionally call
 * `refreshSession()` themselves after the mutation resolves — the hook
 * deliberately does not couple to the auth context.
 */
export function useUpdateMyProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateMyProfileRequest) => updateMyProfile(patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MY_PROFILE_QUERY_KEY });
    },
  });
}

export function useDeleteMyProfilePicture() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMyProfilePicture,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MY_PROFILE_QUERY_KEY });
    },
  });
}
